import { apiClient } from "@/api/client";
import axios, { AxiosError } from "axios";
import { create } from "zustand";

interface CampaignMetadata {
  id: string;
  name: string;
  project_code: string;
  budget: number;
  file_naming_convention: string;
  campaign_poster: string | null;
  status: string;
  progress_status: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

interface Agency {
  relation_id: string;
  media_agency_id: string;
  media_agency_name: string;
}

interface CampaignUser {}

interface Team {
  campaign_team_id: string;
  team_id: string;
  name: string;
  team_type: string;
  all_users: any[];
  campaign_users: CampaignUser[];
}

interface Language {
  campaign_language_id: string;
  language_id: string;
  name: string;
}

interface Parameter {
  parameter_id: string;
  key: string;
  value: string;
  value_json: { key: string; value: string };
}

interface AdRow {
  asset_id: string;
  publisher_id: string;
  ad_type: string;
  versions: number;
  parameters: Parameter[];
}

interface FlightAdCategory {
  flight_ad_categories_id: string;
  ad_category_id: string | null;
  ad_category_name: string | null;
  ad_rows: AdRow[];
}

interface FlightRow {
  flight_row_id: string;
  language: string;
  objective: string;
  region: string;
  flight_ad_categories: FlightAdCategory[];
}

interface Flight {
  flight_id: string;
  flight_number: number;
  start_date: string;
  end_date: string;
  flight_rows: FlightRow[];
}

interface Asset {
  asset_id: string;
  flight_id: string;
  publisher_id: string;
  ad_type: string;
  region: string;
}

interface Metrics {
  total_assets: number;
  unique_publishers: number;
  unique_ad_types: number;
}

interface Objective {
  objective_id: string;
  objective_name: string;
  assets: Asset[];
  metrics: Metrics;
  header_image: {
    id: string;
    key: string;
    language_id: string;
    mime: string;
    objective_id: string;
    type: string;
    url: string;
  };
}

interface LanguageObjective {
  language_id: string;
  language_name: string;
  objectives: Objective[];
}

interface AdTypeFakeCounts {
  impressions: number;
  clicks: number;
}

interface AdType {
  ad_type_id: string;
  ad_type_name: string;
  fake_counts: AdTypeFakeCounts;
  ratio: string | null;
}

interface AdCategory {
  ad_category_id: string;
  ad_category_name: string;
  total_ads: number;
  ad_types: AdType[];
}

interface DashboardLanguage {
  language_id: string;
  name: string;
}

interface DashboardObjective {
  objective_id: string;
  name: string;
}

interface DashboardMetadata {
  campaign: CampaignMetadata;
  agency: Agency[];
  teams: Team[];
  users: any[];
  language: DashboardLanguage;
  objective: DashboardObjective;
}

interface DashboardFlightAdRow {
  asset_id: string;
  publisher_id: string;
  ad_type: string;
  versions: number;
  parameters: Parameter[];
}

interface DashboardFlightAdCategory {
  flight_ad_categories_id: string;
  ad_category_id: string;
  ad_category_name: string;
  ad_rows: DashboardFlightAdRow[];
}

interface DashboardFlightRow {
  flight_row_id: string;
  language: string;
  objective: string;
  region: string;
  flight_ad_categories: DashboardFlightAdCategory[];
}

interface DashboardFlight {
  flight_id: string;
  flight_number: number;
  start_date: string;
  end_date: string;
  flight_rows: DashboardFlightRow[];
}

interface DashboardStats {
  total_assets: number;
  unique_ad_types: number;
}

interface LanguageObjectiveDashboardData {
  metadata: DashboardMetadata;
  nested_by_ad_category: AdCategory[];
  nested_by_flight: DashboardFlight[];
  stats: DashboardStats;
}

interface LanguageObjectiveDashboardResponse {
  success: boolean;
  message: string;
  data: LanguageObjectiveDashboardData;
}

interface CampaignData {
  metadata: {
    campaign: CampaignMetadata;
    agency: Agency[];
    teams: Team[];
    users: any[];
    languages: Language[];
    header_image: {
      id: string;
      key: string;
      mime: string;
      type: string;
      url: string;
    };
  };
  nested_by_flight: Flight[];
  nested_by_language_objective: LanguageObjective[];
}

interface CampaignDetailResponse {
  success: boolean;
  message: string;
  data: CampaignData;
}

interface UploadDashboardImageRequest {
  campaign_id: string;
  type: string;
  key?: string;
  filename?: string;
  mime: string;
}

interface UploadAssetsDashboardImageRequest {
  campaign_id: string;
  type: string;
  objective_id: string;
  language_id: string;
  key?: string;
  filename?: string;
  mime: string;
}

interface UploadDashboardImageResponse {
  success: boolean;
  message: string;
  data: { id: string; key: string; mime: string; type: string; url: string };
}

interface InitiateUploadResponse {
  success: boolean;
  message: string;
  data: { upload_url: string; key: string; mime: string };
}

interface CampaignDetailState {
  campaignDetail: CampaignData | null;
  languageObjectiveDashboard: LanguageObjectiveDashboardData | null;
  getCampaignDetailData: (campaignId: string) => Promise<void>;
  getLanguageObjectiveDashboard: (
    campaignId: string,
    languageId: string,
    objectiveId: string
  ) => Promise<void>;
  putS3Image: (presignedUrl: string, file: File) => Promise<void>;
  uploadDashboardImage: (
    campaignId: string,
    requestBody: UploadDashboardImageRequest
  ) => Promise<UploadDashboardImageResponse | InitiateUploadResponse>;
  uploadAssetsDashboardImage: (
    campaignId: string,
    requestBody: UploadAssetsDashboardImageRequest
  ) => Promise<UploadDashboardImageResponse | InitiateUploadResponse>;
}

const useCampaignDetailStore = create<CampaignDetailState>((set, get) => ({
  campaignDetail: null,
  languageObjectiveDashboard: null,

  getCampaignDetailData: async (campaignId: string) => {
    try {
      const response = await apiClient.get<CampaignDetailResponse>(
        `/campaign/main-info/${campaignId}`
      );
      set({ campaignDetail: response.data.data });
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const message =
        axiosError.response?.data?.error ||
        axiosError.response?.data?.message ||
        "Something went wrong";
      throw new Error(message);
    }
  },

  getLanguageObjectiveDashboard: async (
    campaignId: string,
    languageId: string,
    objectiveId: string
  ) => {
    try {
      const response = await apiClient.get<LanguageObjectiveDashboardResponse>(
        `/campaign-dashboard/${campaignId}/language/${languageId}/objective/${objectiveId}`
      );
      set({ languageObjectiveDashboard: response.data.data });
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const message =
        axiosError.response?.data?.error ||
        axiosError.response?.data?.message ||
        "Failed to fetch language-objective dashboard";
      throw new Error(message);
    }
  },

  putS3Image: async (presignedUrl: string, file: File) => {
    try {
      const res = await axios.put(presignedUrl, file, {
        headers: { "Content-Type": file.type },
      });
      if (res.status !== 200)
        throw new Error(`Unexpected status ${res.status}`);
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("[S3 Upload Error]", axiosError.response?.status);
      throw new Error("Failed to upload image to S3");
    }
  },

  uploadDashboardImage: async (
    campaignId: string,
    requestBody: UploadDashboardImageRequest
  ) => {
    try {
      const response = await apiClient.post<
        UploadDashboardImageResponse | InitiateUploadResponse
      >(`/campaign-dashboard/${campaignId}/confirm-header-image`, requestBody);
      if ("upload_url" in response.data.data)
        return response.data as InitiateUploadResponse;
      return response.data as UploadDashboardImageResponse;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const message =
        axiosError.response?.data?.error ||
        axiosError.response?.data?.message ||
        "Failed to upload dashboard image";
      throw new Error(message);
    }
  },

  uploadAssetsDashboardImage: async (
    campaignId: string,
    requestBody: UploadAssetsDashboardImageRequest
  ) => {
    try {
      const response = await apiClient.post<
        UploadDashboardImageResponse | InitiateUploadResponse
      >(`/campaign-dashboard/${campaignId}/confirm-header-image`, requestBody);
      if ("upload_url" in response.data.data)
        return response.data as InitiateUploadResponse;
      return response.data as UploadDashboardImageResponse;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const message =
        axiosError.response?.data?.error ||
        axiosError.response?.data?.message ||
        "Failed to upload assets dashboard image";
      throw new Error(message);
    }
  },
}));

export default useCampaignDetailStore;
export type { AdCategory, AdType, LanguageObjectiveDashboardData };
